import { UserSettings } from "@/hooks/use-settings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SettingRow = ({ label, description, children }: { label: string; description: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <p className="text-[11px] text-muted-foreground">{description}</p>
    </div>
    {children}
  </div>
);

interface Props {
  settings: UserSettings;
  updateSetting: <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => void;
}

const DuctworkSettings = ({ settings, updateSetting }: Props) => (
  <section className="space-y-3">
    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ductwork Defaults</h2>
    <div className="trade-card space-y-4">
      <SettingRow label="Default Velocity" description="Starting velocity for duct sizing calcs">
        <Select value={settings.ductVelocity} onValueChange={(v) => updateSetting("ductVelocity", v as UserSettings["ductVelocity"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="400">400 fpm (Res. Return)</SelectItem>
            <SelectItem value="600">600 fpm (Res. Supply)</SelectItem>
            <SelectItem value="800">800 fpm (Com. Return)</SelectItem>
            <SelectItem value="1200">1200 fpm (Com. Supply)</SelectItem>
            <SelectItem value="1500">1500 fpm (Industrial)</SelectItem>
            <SelectItem value="2500">2500 fpm (High Vel.)</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>

      <SettingRow label="Duct Material" description="Default material assumption for calcs">
        <Select value={settings.ductMaterial} onValueChange={(v) => updateSetting("ductMaterial", v as UserSettings["ductMaterial"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="galvanized">Galvanized Steel</SelectItem>
            <SelectItem value="aluminum">Aluminum</SelectItem>
            <SelectItem value="flex">Flex Duct</SelectItem>
            <SelectItem value="fiberglass">Fiberglass</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>

      <SettingRow label="SMACNA Seal Class" description="Default leakage class for duct calcs">
        <Select value={settings.sealClass} onValueChange={(v) => updateSetting("sealClass", v as UserSettings["sealClass"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unsealed">
              <span>Unsealed</span>
              <span className="ml-2 text-[10px] text-muted-foreground">CL 48</span>
            </SelectItem>
            <SelectItem value="C">
              <span>Class C</span>
              <span className="ml-2 text-[10px] text-muted-foreground">CL 24</span>
            </SelectItem>
            <SelectItem value="B">
              <span>Class B</span>
              <span className="ml-2 text-[10px] text-muted-foreground">CL 12</span>
            </SelectItem>
            <SelectItem value="A">
              <span>Class A</span>
              <span className="ml-2 text-[10px] text-muted-foreground">CL 6</span>
            </SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
    </div>
  </section>
);

export default DuctworkSettings;
